import Link from '@/components/AppLink';
import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "@/helpers/axios";
import { OverviewSection } from "../overview-section";
import panelStates from "../overview-panel-states/OverviewPanelStates.module.scss";
import styles from "./OverviewPendingModelsSection.module.scss";

const MAX_VERSIONS = 4;

type ReviewRow = {
  id: string;
  species_slug: string;
  species_display_name: string;
  version_label: string;
  updated_at: number;
  change_description: string | null;
};

export const OverviewPendingModelsSection = () => {
  const [queue, setQueue] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/v2/models/versions", {
        params: { status: "in_review" },
      });
      const items = data?.data?.items as ReviewRow[] | undefined;
      setQueue(Array.isArray(items) ? items.slice(0, MAX_VERSIONS) : []);
    } catch {
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <OverviewSection
      title="Model versions to review"
      href="/models/review"
    >
      {loading ? (
        <div className={panelStates["state"]}>Loading…</div>
      ) : queue.length === 0 ? (
        <div className={panelStates["state"]}>Nothing waiting for review.</div>
      ) : (
        <div className={styles["list"]}>
          {queue.map((row) => (
            <Link
              key={row.id}
              href={`/models/${row.species_slug}`}
              className={styles["row"]}
            >
              <div className={styles["row__main"]}>
                <div className={styles["row__title"]}>
                  {row.species_display_name} · {row.version_label}
                </div>
                <div className={styles["row__meta"]}>
                  Updated {new Date(row.updated_at).toLocaleString()}
                </div>
                {row.change_description ? (
                  <p className={styles["row__notes"]}>{row.change_description}</p>
                ) : null}
              </div>
              <span className={styles["badge"]}>in_review</span>
            </Link>
          ))}
        </div>
      )}
    </OverviewSection>
  );
};
